import { useState } from "react";
import { Plus, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";

const empty = { name: "", photo: "", role: "", bio: "", social_link: "" };

export const ArtistManager = ({ artists, reload }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const open = (a) => { setEditing(a || "new"); setForm(a ? { ...a } : empty); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing === "new") await api.post("/admin/artists", form);
      else await api.put(`/admin/artists/${editing.id}`, form);
      toast.success("Artist saved");
      close(); reload();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/artists/${id}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="artist-manager">
      <div className="mb-5 flex justify-end">
        <button onClick={() => open(null)} className="btn-gold !py-2.5 text-sm" data-testid="add-artist-button"><Plus size={15} /> Add Artist</button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {artists.map((a) => (
          <div key={a.id} className="card-dark flex items-center gap-4 p-4" data-testid={`artist-item-${a.id}`}>
            <img src={a.photo ? fileUrl(a.photo) : "https://images.unsplash.com/photo-1544785349-c4a5301826fd?w=200&q=60"} alt="" className="h-14 w-14 rounded-full object-cover" />
            <div className="flex-1">
              <p className="font-medium">{a.name}</p>
              <p className="text-xs text-zinc-500">{a.role}</p>
            </div>
            <button onClick={() => open(a)} className="text-zinc-400 hover:text-[#E5C158]" data-testid={`edit-artist-${a.id}`}><Edit size={16} /></button>
            <button onClick={() => del(a.id)} className="text-red-400" data-testid={`delete-artist-${a.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
        {!artists.length && <p className="text-sm text-zinc-500">No artists yet.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/80 p-4 py-10" data-testid="artist-form-modal">
          <div className="card-dark w-full max-w-lg p-6 sm:p-8">
            <h2 className="mb-5 font-serif text-2xl font-bold">{editing === "new" ? "Add Artist" : "Edit Artist"}</h2>
            <div className="space-y-4">
              <div><label className="caption mb-2 block">Name *</label><input className="input-field" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="artist-name-input" /></div>
              <ImageUpload label="Photo" value={form.photo} onChange={(v) => set("photo", v)} testid="artist-photo-upload" />
              <div><label className="caption mb-2 block">Role</label><input className="input-field" placeholder="e.g. DJ, Vocalist" value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="artist-role-input" /></div>
              <div><label className="caption mb-2 block">Short Bio</label><textarea rows={3} className="input-field" value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="artist-bio-input" /></div>
              <div><label className="caption mb-2 block">Social Link</label><input className="input-field" value={form.social_link} onChange={(e) => set("social_link", e.target.value)} data-testid="artist-social-input" /></div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} className="btn-gold flex-1" data-testid="artist-save-button">SAVE</button>
              <button onClick={close} className="btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
